// public/v3/src/lib/order-chat-patient-pin.js
// Pure helpers for the Order Chat patient pin-picker. Framework-free and
// CommonJS so they're unit-testable under Jest (this repo has no JSX/ESM
// transform); order-chat.jsx imports them via esbuild's CJS interop.

// Find the end index (and start) of the LAST "for " in the string. Word-bounded
// so "before" doesn't match. Returns { start, end } or null.
function _lastForBounds(s) {
  const re = /\bfor\s+/gi;
  let last = null, m;
  while ((m = re.exec(s))) last = { start: m.index, end: m.index + m[0].length };
  return last;
}

// The patient-name fragment the provider is currently typing: everything after
// the LAST "for ". '' when there's no "for " or nothing typed after it. Only
// trailing whitespace is trimmed (interior kept, e.g. "john s").
function patientFragment(input) {
  const s = String(input || '');
  const b = _lastForBounds(s);
  if (!b) return '';
  return s.slice(b.end).replace(/\s+$/, '');
}

// Replace the patient-NAME portion of the trailing fragment (after the last
// "for ") with the canonical "First Last", PRESERVING any order text after the
// name — a pasted full line ("... for mitchel weissberg from northwell health
// labs") must not lose its tail when the provider clicks the suggestion. The
// name portion is measured word-by-word: leading fragment words are consumed
// while they match the canonical name's words in order (prefixes count both
// ways, so a partially-typed "mitch" and an over-typed "weissbergg" both
// consume). Nothing consumed (the pick doesn't resemble the fragment at all)
// falls back to replacing the whole fragment — the pre-paste behavior, where
// the fragment WAS the search query. If there's no "for ", append the name.
function applyCanonicalName(input, fullName) {
  const s = String(input || '');
  const b = _lastForBounds(s);
  if (!b) {
    const base = s.replace(/\s+$/, ''); // drop any trailing whitespace first
    return base ? `${base} for ${fullName}` : `for ${fullName}`;
  }
  const tail = s.slice(b.end);
  const nameToks = String(fullName || '').split(/\s+/).filter(Boolean).map(_norm);
  const wordRe = /\S+/g;
  let consumedEnd = 0;
  for (let i = 0; i < nameToks.length; i++) {
    const m = wordRe.exec(tail);
    if (!m) break;
    const w = _norm(m[0]);
    if (!(nameToks[i].startsWith(w) || w.startsWith(nameToks[i]))) break;
    consumedEnd = m.index + m[0].length;
  }
  if (consumedEnd === 0) return s.slice(0, b.end) + fullName;
  const rest = tail.slice(consumedEnd);
  return s.slice(0, b.end) + fullName + (rest.trim() ? rest : '');
}

// Compare case-, whitespace-, and Unicode-insensitively so a cosmetically
// equal name (accents typed NFD vs Elation's NFC, a stray double space)
// doesn't falsely clear the pin while the provider is mid-typing.
const _norm = s => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase().normalize('NFC');
const _escRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// True when the typed fragment IS the pinned full name, or extends past it into
// order text ("john smith cbc"). Boundary-aware: "john smithson" is a DIFFERENT,
// longer name — not an extension. Used to suppress the type-ahead dropdown once
// the complete name is in the box (typing the order body must not re-search).
function fragmentCoversPin(frag, pinnedFullName) {
  if (!pinnedFullName) return false;
  const f = _norm(frag), name = _norm(pinnedFullName);
  if (!f) return false;
  // SAME boundary rule as pinDiverged: the full name, then end-of-string or a
  // non-alphanumeric ("john smith cbc", "john smith, fasting"). A mismatched
  // boundary here would reopen the dropdown over punctuation and let a reselect
  // truncate the typed text.
  return new RegExp(`^${_escRe(name)}(?=$|[^a-z0-9])`).test(f);
}

// True when the typed patient fragment has diverged from the pinned chart and
// the pin should auto-clear. Keeps the pin when the box is empty or has no
// "for " (so it survives the post-send clear and clarification round-trips),
// while the fragment is still a prefix of the pinned name (mid-typing), AND —
// crucially — once the complete "for First Last" phrase is intact anywhere in
// the input: typing the order body after the name ("... for John Smith cbc",
// or a second clause "... for John Smith for cbc") must NOT drop the pin.
function pinDiverged(input, pinnedFullName) {
  if (!pinnedFullName) return false;
  const s = _norm(input), name = _norm(pinnedFullName);
  // Canonical word-bounded "for <full name>" still present → pinned, regardless
  // of what follows (order text, another "for ..." clause).
  if (new RegExp(`\\bfor ${_escRe(name)}(?=$|[^a-z0-9])`).test(s)) return false;
  const frag = patientFragment(input);
  if (!frag) return false;
  return !name.startsWith(_norm(frag));
}

// How many patients the typed text already names, counted CLIENT-LOCALLY (O85):
// word-bounded "for " clauses with at least two characters of a name after them.
// A clause that opens with a digit is an instruction, not a person ("fasting for
// 12 hours for john s" names one patient, not two). Two or more clauses means
// there is no single patient to pin, so the picker never opens — deliberately
// blunt: the pin's danger is structural (one pin, two subjects), not
// identity-dependent, and a suppressed picker costs the provider nothing but
// typing the full name, which they were doing anyway.
function namedPatientClauses(input) {
  const s = String(input || '');
  const re = /\bfor\s+/gi;
  const bounds = [];
  let m;
  while ((m = re.exec(s))) bounds.push({ start: m.index, end: m.index + m[0].length });
  let n = 0;
  for (let i = 0; i < bounds.length; i++) {
    const stop = i + 1 < bounds.length ? bounds[i + 1].start : s.length;
    const clause = s.slice(bounds[i].end, stop).trim();
    // A digit opens an instruction, never a person. Everything else may be a
    // name — an accented or non-Latin initial included, which an allowlist of
    // plain letters would have quietly excluded.
    if (clause.length >= 2 && !/^\d/.test(clause)) n += 1;
  }
  return n;
}

// The ONE place that decides whether the type-ahead may search at all, so the
// page's effect and this file's witnesses cannot disagree about it. Returns the
// fragment to search, or null when the picker must stay shut: nothing typed yet
// (under two characters), the pinned patient's name already covered by the typed
// text, or two patients already named (O85).
function patientSearchFragment(input, pinnedFullName) {
  const frag = patientFragment(input);
  if (frag.length < 2) return null;
  if (fragmentCoversPin(frag, pinnedFullName)) return null;
  if (namedPatientClauses(input) >= 2) return null;
  return frag;
}

// What a keystroke in the composer MEANS (O83). Enter belongs to send unless the
// provider arrowed into the list themselves — activeIndex is -1 until they do,
// and back to -1 on every fresh result set — so the picker can no longer swallow
// the keystroke and pin a patient nobody chose. Clicking a row still pins; that
// is explicit. Pure, so the page's handler has exactly one rule and a witness
// can drive it without a DOM.
function composerKeyAction({ key, shiftKey, suggestOpen, count, activeIndex }) {
  const listUp = !!suggestOpen && Number(count) > 0;
  if (listUp && !shiftKey) {
    if (key === 'ArrowDown') return 'move-down';
    if (key === 'ArrowUp') return 'move-up';
    if (key === 'Escape') return 'close';
    // The row has to exist: a stale index past the end of a newly-shortened
    // list would otherwise pin `undefined`.
    if (key === 'Enter' && activeIndex >= 0 && activeIndex < Number(count)) return 'pin';
  }
  if (key === 'Enter' && !shiftKey) return 'send';
  return 'default';
}

// "1950-03-12" (or a full timestamp) -> "03/12/1950". '' for falsy/unparseable.
function formatDob(dob) {
  if (!dob) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob));
  if (!m) return ''; // unrecognised shape → render nothing, not garbage
  return `${m[2]}/${m[3]}/${m[1]}`;
}

module.exports = {
  patientFragment, applyCanonicalName, pinDiverged, fragmentCoversPin, formatDob,
  namedPatientClauses, patientSearchFragment, composerKeyAction,
};
